import { AnimatePresence, motion } from "framer-motion";
import { useHeaderButton, usePrimaryNav, useSettings } from "@/content-context";
import { Link } from "react-router";
import { useLocation } from "react-router";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import { Logo } from "./Logo";
import { easeOut } from "@/lib/motion";

/**
 * Full-screen mobile navigation.
 *
 * A sheet rather than a dropdown: one column, generous spacing, links staggered
 * in. Escape closes and restores focus to the trigger, Tab is trapped, and
 * background scroll is locked while open.
 */
export function MobileNav({ overHero }: { overHero: boolean }) {
  const primaryNav = usePrimaryNav();
  const headerButton = useHeaderButton();
  const { socialLinks } = useSettings();
  const [open, setOpen] = useState(false);
  const pathname = useLocation().pathname;
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const timer = window.setTimeout(
      () => panelRef.current?.querySelector<HTMLElement>("a[href]")?.focus(),
      80,
    );

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(timer);
    };
  }, [open, close]);

  const flat = primaryNav.flatMap((entry) => [
    { label: entry.label, href: entry.href, child: false },
    ...(entry.children ?? []).map((c) => ({ label: c.label, href: c.href, child: true })),
  ]);
  const isActive = (href: string) => pathname === href || pathname.startsWith(`${href}/`);

  return (
    <div className="lg:hidden">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => (open ? close() : setOpen(true))}
        className={`inline-flex h-11 w-11 items-center justify-center rounded-full border transition-colors duration-300 ${
          open
            ? "border-line bg-white text-midnight"
            : overHero
              ? "border-white/30 text-white hover:bg-white/10"
              : "border-line bg-white text-midnight"
        }`}
      >
        <span className="sr-only">{open ? "Close menu" : "Open menu"}</span>
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5">
          {open ? (
            <path
              d="m6 6 12 12M18 6 6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          ) : (
            <path
              d="M4 8.5h16M4 15.5h16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          )}
        </svg>
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            id={panelId}
            ref={panelRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            transition={{ duration: 0.3, ease: easeOut }}
            className="fixed inset-0 z-50 overflow-y-auto bg-white"
          >
            <div className="container-page flex min-h-full flex-col py-5">
              <div className="flex items-center justify-between">
                <Logo />
                <button
                  type="button"
                  onClick={close}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line text-midnight"
                >
                  <span className="sr-only">Close menu</span>
                  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5">
                    <path
                      d="m6 6 12 12M18 6 6 18"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>

              <nav aria-label="Primary (mobile)" className="mt-12 flex-1">
                <ul>
                  {flat.map((item, i) => (
                    <motion.li
                      key={item.href + item.label}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.45, delay: 0.05 + i * 0.028, ease: easeOut }}
                    >
                      <Link
                        to={item.href}
                        aria-current={isActive(item.href) ? "page" : undefined}
                        className={`flex min-h-12 items-center border-b border-line ${
                          item.child
                            ? `pl-5 text-[0.9375rem] ${isActive(item.href) ? "bg-mist font-semibold text-navy" : "text-muted"}`
                            : `font-serif text-[1.5rem] ${isActive(item.href) ? "bg-mist pl-3 text-navy" : "text-midnight"}`
                        }`}
                      >
                        {item.label}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </nav>

              <div className="mt-10 pb-6">
                {headerButton ? (
                  <Link
                    to={headerButton.href}
                    className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-navy px-6 text-[0.9375rem] font-semibold text-white"
                  >
                    {headerButton.label}
                  </Link>
                ) : null}
                <ul className="mt-7 flex flex-wrap gap-x-5 gap-y-1">
                  {socialLinks.map((social) => (
                    <li key={social.href}>
                      <a
                        href={social.href}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex min-h-11 items-center text-[0.8125rem] text-muted"
                      >
                        {social.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
